var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import MuiButton from '@mui/material/Button';
import { forwardRef } from 'react';
import colors from '../../colors';
import * as Icons from '../../icons';
import { applyEventProps } from '../../utils/props';
import Spinner from '../Spinner';
export var convertVariant = function (variant) {
    switch (variant) {
        case 'filled':
            return 'contained';
        case 'outlined':
            return 'outlined';
        case 'flat':
            return 'text';
        case 'link':
            return 'link';
        default:
            return undefined;
    }
};
export var convertTypeToColor = function (type, variant) {
    if (variant === 'flat') {
        return type;
    }
    return type === 'secondary' ? 'primary' : type;
};
var convertSize = function (size) {
    switch (size) {
        case 'large':
            return 'large';
        case 'small':
            return 'medium';
        case 'tiny':
            return 'small';
        default:
            return undefined;
    }
};
var convertIconSize = function (size) {
    switch (size) {
        case 'large':
        case 'small':
            return 'default';
        case 'tiny':
            return 'tiny';
        default:
            return undefined;
    }
};
export var applyPressedCss = function (variant, type, pressed) {
    if (!pressed) {
        return {};
    }
    switch (variant) {
        case 'filled':
            return {
                backgroundColor: colors.blue[600],
            };
        case 'outlined':
            return {
                backgroundColor: colors.grey[100],
            };
        case 'flat':
            if (type === 'secondary') {
                return { color: colors.grey[900] };
            }
            else {
                return { color: colors.blue[600] };
            }
        case 'link':
            return {
                color: colors.blue[600],
            };
        default:
            return {};
    }
};
var Button = forwardRef(function (props, ref) {
    var children = props.children, _a = props.variant, variant = _a === void 0 ? 'filled' : _a, _b = props.size, size = _b === void 0 ? 'large' : _b, _c = props.disabled, disabled = _c === void 0 ? false : _c, _d = props.type, type = _d === void 0 ? 'primary' : _d, _e = props.loading, loading = _e === void 0 ? false : _e, startIconName = props.startIconName, endIconName = props.endIconName, onClick = props.onClick, pressed = props.pressed, _f = props.htmlType, htmlType = _f === void 0 ? 'button' : _f, dataTestId = props.dataTestId, rest = __rest(props, ["children", "variant", "size", "disabled", "type", "loading", "startIconName", "endIconName", "onClick", "pressed", "htmlType", "dataTestId"]);
    var getIconColor = function () {
        if (disabled) {
            return colors.grey[500];
        }
        if (variant === 'filled') {
            return colors.white;
        }
        if (type === 'error') {
            return colors.blush[500];
        }
        if (variant === 'outlined') {
            return colors.grey[800];
        }
        if (variant === 'flat') {
            if (type === 'primary') {
                return colors.blue[500];
            }
            if (type === 'secondary') {
                return colors.grey[800];
            }
        }
        if (variant === 'link') {
            return colors.blue[500];
        }
    };
    var renderIcon = function (iconName) {
        var _a;
        var StartIcon = iconName && Icons[iconName];
        if (StartIcon) {
            return (_jsx(StartIcon, { variant: convertIconSize(size), color: (_a = getIconColor()) !== null && _a !== void 0 ? _a : colors.blush[500] }));
        }
        return null;
    };
    var renderLoadingButton = function () {
        return (_jsx(MuiButton, __assign({}, rest, { ref: ref, variant: convertVariant(variant), size: convertSize(size), disabled: disabled, color: convertTypeToColor(type, variant), type: htmlType, "data-testid": dataTestId }, { children: _jsx(Spinner, { color: getIconColor() }) })));
    };
    var renderRegularButton = function () {
        return (_jsx(MuiButton, __assign({}, rest, applyEventProps(props), { ref: ref, variant: convertVariant(variant), size: convertSize(size), disabled: disabled, onClick: onClick, color: convertTypeToColor(type, variant), startIcon: renderIcon(startIconName), endIcon: renderIcon(endIconName), type: htmlType, sx: __assign({}, applyPressedCss(variant, type, pressed)), "data-testid": dataTestId }, { children: children })));
    };
    return loading ? renderLoadingButton() : renderRegularButton();
});
export default Button;
